import React, { cloneElement, isValidElement, useState, useContext, useEffect, useId } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import './profile.css'; // Import the CSS file

import { AuthContext } from '_store';
import { apiFetch } from 'api/client';
import { ThemeContext, normalizeColorScheme } from 'theme';

export { Profile };

function Profile() {
    const { authUser, setAuthUser, userRole } = useContext(AuthContext);
    const { colorScheme, setColorScheme } = useContext(ThemeContext);
    const [authError, setAuthError] = useState(null);
    const [authSuccess, setAuthSuccess] = useState(null);
    const [prefTimeRange, setPrefTimeRange] = useState('');
    const [prefOrgId, setPrefOrgId] = useState('');
    const [prefColorScheme, setPrefColorScheme] = useState(colorScheme);
    const [prefLoading, setPrefLoading] = useState(false);
    const [prefSaving, setPrefSaving] = useState(false);
    const [prefMessage, setPrefMessage] = useState(null);

    // Support both legacy string value and object shape for authUser
    const username = authUser?.username || '';
    const organizations = (authUser && typeof authUser === 'object' && Array.isArray(authUser.organizations))
        ? authUser.organizations
        : [];

    const timeRangeOptions = [
        { label: 'Last 1 hour', value: '1' },
        { label: 'Last 3 hours', value: '3' },
        { label: 'Last 6 hours', value: '6' },
        { label: 'Last 12 hours', value: '12' },
        { label: 'Last 24 hours', value: '24' },
        { label: 'Last 2 days', value: '48' },
        { label: 'Last 7 days', value: '168' },
        { label: 'Last 30 days', value: '720' }
    ];

    // Form validation schema
    const validationSchema = Yup.object().shape({
        currentPassword: Yup.string().required('Current Password is required'),
        newPassword: Yup.string().required('New Password is required').min(6, 'Password must be at least 6 characters'),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
            .required('Confirm Password is required')
    });

    const formOptions = { resolver: yupResolver(validationSchema) };
    const { register, handleSubmit, formState, reset } = useForm(formOptions);
    const { errors, isSubmitting } = formState;

    // Heuristic: detect OIDC / external Authentik user (adjust keys if backend uses different field names)
    const lower = (v) => (typeof v === 'string' ? v.toLowerCase() : '');
    const providerHints = [
        lower(authUser?.provider),
        lower(authUser?.auth_provider),
        lower(authUser?.source),
        lower(authUser?.identityProvider),
        lower(authUser?.authSource)
    ];
    const isOidcUser = providerHints.some(v => ['oidc','openid','authentik'].includes(v));

    useEffect(() => {
        async function fetchDefaultPreferences() {
            setPrefLoading(true);
            setPrefMessage(null);

            try {
                const response = await apiFetch('/auth/default-preferences', {
                    method: 'GET',
                });

                if (!response.ok) {
                    throw new Error('Failed to load default preferences');
                }

                const data = await response.json();
                const timeRangeValue = data?.default_time_range ?? data?.defaultTimeRange ?? data?.timeRange ?? '';
                const orgValue = data?.default_org_id ?? data?.defaultOrgId ?? '';
                const schemeValue = normalizeColorScheme(data?.color_scheme ?? data?.colorScheme);

                if (timeRangeValue) {
                    setPrefTimeRange(String(timeRangeValue));
                }

                if (orgValue != null && orgValue !== '') {
                    setPrefOrgId(String(orgValue));
                }
                setPrefColorScheme(schemeValue);
            } catch (error) {
                console.error('Default preferences fetch error:', error);
                setPrefMessage({ type: 'error', text: error.message || 'Unable to load preferences' });
            } finally {
                setPrefLoading(false);
            }
        }

        fetchDefaultPreferences();
    }, []);

    async function onSubmit(data) {
        const { currentPassword, newPassword } = data;

        try {
            setAuthError(null);
            const response = await apiFetch('/auth/change-password', {
                method: 'POST',
                json: {
                    username: username,
                    password: currentPassword,
                    newpassword: newPassword
                }
            });

            if (!response.ok) {
                throw new Error('Failed to change password');
            }

            setAuthSuccess('Password changed successfully.');
            reset();
        } catch (error) {
            setAuthError(error.message);
        }
    }

    const handlePrefSubmit = async (event) => {
        event.preventDefault();
        if (!prefTimeRange) {
            setPrefMessage({ type: 'error', text: 'Please select a default time range.' });
            return;
        }

        if (organizations.length > 0 && !prefOrgId) {
            setPrefMessage({ type: 'error', text: 'Please select a default organization.' });
            return;
        }

        try {
            setPrefSaving(true);
            setPrefMessage(null);

            const response = await apiFetch('/auth/default-preferences', {
                method: 'POST',
                json: {
                    default_time_range: prefTimeRange,
                    default_org_id: prefOrgId ? Number(prefOrgId) : null,
                    color_scheme: prefColorScheme,
                }
            });

            if (!response.ok) {
                throw new Error('Failed to save default preferences');
            }

            setColorScheme(prefColorScheme);
            setPrefMessage({ type: 'success', text: 'Default preferences saved successfully.' });

            if (setAuthUser && authUser && typeof authUser === 'object') {
                setAuthUser((prevAuthUser) => {
                    if (!prevAuthUser || typeof prevAuthUser !== 'object') {
                        return prevAuthUser;
                    }

                    const normalizedOrgId = prefOrgId ? Number(prefOrgId) : null;

                    return {
                        ...prevAuthUser,
                        default_time_range: prefTimeRange,
                        defaultTimeRange: prefTimeRange,
                        default_org_id: normalizedOrgId,
                        defaultOrgId: normalizedOrgId,
                        color_scheme: prefColorScheme,
                        colorScheme: prefColorScheme,
                    };
                });
            }
        } catch (error) {
            console.error('Default preferences save error:', error);
            setPrefMessage({ type: 'error', text: error.message || 'Unable to save preferences.' });
        } finally {
            setPrefSaving(false);
        }
    };

    return (
        <div className="profile-page">
            {/* ── Hero header ───────────────────────────────────── */}
            <div className="profile-hero">
                <div>
                    <div className="profile-breadcrumb">
                        <span className="profile-breadcrumb-dot"></span>
                        <span className="profile-breadcrumb-text">Account / Profile</span>
                    </div>
                    <h1 className="profile-title">Profile</h1>
                </div>
            </div>

            {/* ── Identity card ─────────────────────────────────── */}
            <div className="profile-identity-card">
                <div className="profile-avatar">
                    <span className="material-symbols-outlined">account_circle</span>
                </div>
                <div className="profile-identity-info">
                    <div className="profile-identity-name">{username || '—'}</div>
                    <div className="profile-identity-meta">
                        <span className="profile-role-badge">{(userRole && userRole.trim()) ? userRole : 'user'}</span>
                        {organizations.length > 0 && (
                            <span className="profile-identity-orgs">
                                {organizations.map(o => o.name || o.displayName || o.id || String(o)).join(', ')}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Two-column grid ───────────────────────────────── */}
            <div className="profile-grid profile-grid-2col">

                {/* ── Change Password ───────────────────────────── */}
                <div className="profile-card">
                    <div className="profile-card-header">
                        <span className="material-symbols-outlined profile-card-icon">lock</span>
                        <div>
                            <h2 className="profile-card-title">Change Password</h2>
                            <p className="profile-card-desc">Update your account password.</p>
                        </div>
                    </div>

                    {isOidcUser && (
                        <div className="profile-info-banner">
                            <span className="material-symbols-outlined text-sm">info</span>
                            Password is managed by your identity provider (Authentik) and cannot be changed here.
                        </div>
                    )}

                    <form onSubmit={handleSubmit(onSubmit)} className="profile-form-fields">
                        <ProfileField label="Current Password" error={errors.currentPassword?.message}>
                            <input type="password" {...register('currentPassword')} disabled={isOidcUser}
                                className={`profile-input${errors.currentPassword ? ' error' : ''}${isOidcUser ? ' disabled' : ''}`} />
                        </ProfileField>
                        <ProfileField label="New Password" error={errors.newPassword?.message}>
                            <input type="password" {...register('newPassword')} disabled={isOidcUser}
                                className={`profile-input${errors.newPassword ? ' error' : ''}${isOidcUser ? ' disabled' : ''}`} />
                        </ProfileField>
                        <ProfileField label="Confirm New Password" error={errors.confirmPassword?.message}>
                            <input type="password" {...register('confirmPassword')} disabled={isOidcUser}
                                className={`profile-input${errors.confirmPassword ? ' error' : ''}${isOidcUser ? ' disabled' : ''}`} />
                        </ProfileField>

                        {authError && <ProfileError msg={authError} />}
                        {authSuccess && (
                            <div className="profile-message success">
                                <span className="material-symbols-outlined text-sm">check_circle</span>
                                {authSuccess}
                            </div>
                        )}

                        <button type="submit" disabled={isSubmitting || isOidcUser} className="profile-btn-primary"
                            onClick={() => { setAuthError(null); setAuthSuccess(null); }}>
                            {isSubmitting
                                ? <><span className="material-symbols-outlined text-sm lists-spin">sync</span> Saving…</>
                                : <><span className="material-symbols-outlined text-sm">save</span> Change Password</>
                            }
                        </button>
                    </form>
                </div>

                {/* ── Preferences ───────────────────────────────── */}
                <div className="profile-card">
                    <div className="profile-card-header">
                        <span className="material-symbols-outlined profile-card-icon">tune</span>
                        <div>
                            <h2 className="profile-card-title">Preferences</h2>
                            <p className="profile-card-desc">Configure dashboard defaults for your account.</p>
                        </div>
                    </div>

                    <form onSubmit={handlePrefSubmit} className="profile-form-fields">
                        {prefLoading && (
                            <p className="profile-hint">
                                <span className="material-symbols-outlined text-sm lists-spin">sync</span> Loading preferences…
                            </p>
                        )}

                        <ProfileField label="Default Time Range">
                            <select
                                value={prefTimeRange}
                                onChange={e => { setPrefTimeRange(e.target.value); setPrefMessage(null); }}
                                disabled={prefLoading || prefSaving}
                                className="profile-select"
                            >
                                <option value="" disabled>Choose a time range</option>
                                {timeRangeOptions.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </ProfileField>

                        <ProfileField label="Default Organization">
                            {organizations.length === 0 ? (
                                <p className="profile-hint">No organizations assigned.</p>
                            ) : (
                                <select
                                    value={prefOrgId}
                                    onChange={e => { setPrefOrgId(e.target.value); setPrefMessage(null); }}
                                    disabled={prefLoading || prefSaving}
                                    className="profile-select"
                                >
                                    <option value="" disabled>Select an organization</option>
                                    {organizations.map(org => (
                                        <option key={org.id ?? org.name} value={org.id ?? org.name}>
                                            {org.name || org.displayName || org.id}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </ProfileField>

                        <ProfileField label="Color scheme">
                            <select
                                value={prefColorScheme}
                                onChange={e => { setPrefColorScheme(normalizeColorScheme(e.target.value)); setPrefMessage(null); }}
                                disabled={prefLoading || prefSaving}
                                className="profile-select"
                            >
                                <option value="system">System (use device setting)</option>
                                <option value="light">Light</option>
                                <option value="dark">Dark</option>
                            </select>
                        </ProfileField>

                        {prefMessage && (
                            <div className={`profile-message ${prefMessage.type}`}>
                                <span className="material-symbols-outlined text-sm">
                                    {prefMessage.type === 'success' ? 'check_circle' : 'error'}
                                </span>
                                {prefMessage.text}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={prefSaving || prefLoading || !prefTimeRange || (organizations.length > 0 && !prefOrgId)}
                            className="profile-btn-primary"
                        >
                            {prefSaving
                                ? <><span className="material-symbols-outlined text-sm lists-spin">sync</span> Saving…</>
                                : <><span className="material-symbols-outlined text-sm">save</span> Save Preferences</>
                            }
                        </button>
                    </form>
                </div>

            </div>
        </div>
    );
}

// ── Shared field wrapper ───────────────────────────────────────────
function ProfileField({ label, required, error, children }) {
    const generatedId = useId();
    const isControl = isValidElement(children)
        && typeof children.type === 'string'
        && ['input', 'select', 'textarea'].includes(children.type);
    const controlId = isControl ? (children.props.id || generatedId) : undefined;
    const errorId = error ? `${generatedId}-error` : undefined;
    const describedBy = [isControl ? children.props['aria-describedby'] : null, errorId].filter(Boolean).join(' ') || undefined;
    const fieldControl = isControl ? cloneElement(children, {
        id: controlId,
        required: required || children.props.required,
        'aria-invalid': error ? 'true' : children.props['aria-invalid'],
        'aria-describedby': describedBy,
    }) : children;

    return (
        <div className="profile-field">
            <label className="profile-field-label" htmlFor={controlId}>{label}{required && ' *'}</label>
            {fieldControl}
            {error && <span id={errorId} className="profile-field-error" role="alert">{error}</span>}
        </div>
    );
}

function ProfileError({ msg }) {
    return (
        <div className="profile-error-banner">
            <span className="material-symbols-outlined text-sm">error</span>
            {msg}
        </div>
    );
}
